/**
 * PdfExportPanel — UI panel for configuring and triggering PDF export.
 *
 * Renders language selector, colour scheme selector, and Export button.
 * Only shown when chartResult is available.
 *
 * @module PdfExportPanel
 */
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { pdf } from '@react-pdf/renderer';
import HoroscopePdfDocument from './HoroscopePdfDocument';
import { schemeOptions } from './pdfColorSchemes';

/* ── Styled Components ── */

const PanelContainer = styled.div`
  background: #faf7f0;
  border: 1px solid #d4b896;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(139, 90, 43, 0.12);
  padding: 16px 20px;
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
`;

const SelectorGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectorLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b4c2a;
`;

const OptionBtn = styled.button`
  padding: 5px 12px;
  border: 1px solid ${p => p.active ? '#B8860B' : '#c4a47a'};
  background: ${p => p.active ? '#B8860B' : '#fff9f2'};
  color: ${p => p.active ? '#ffffff' : '#6b4c2a'};
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: ${p => p.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #B8860B;
    background: ${p => p.active ? '#9a720a' : '#fff3e0'};
  }
`;

const ExportButton = styled.button`
  padding: 8px 20px;
  background: ${p => p.disabled ? '#c4a47a' : '#B8860B'};
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  margin-left: auto;
  opacity: ${p => p.disabled ? '0.6' : '1'};
  &:hover:not(:disabled) {
    background: #9a720a;
  }
`;

const ErrorText = styled.span`
  font-size: 0.8rem;
  color: #c62828;
`;

/* ── Filename builder ── */

const buildFilename = (chartResult) => {
  const name = (chartResult?.name || 'chart')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/gi, '')
    .replace(/\s+/g, '-')
    .substring(0, 30);
  const dob = chartResult?.birth_details?.date || 'unknown';
  return `horoscope-${name}-${dob}.pdf`;
};

/* ── Component ── */

const PdfExportPanel = ({ chartResult, lang }) => {
  const [pdfLang, setPdfLang] = useState(lang || 'en');
  const [colorScheme, setColorScheme] = useState('light');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const isTa = lang === 'ta';

  const handleExport = useCallback(async () => {
    if (!chartResult || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const doc = (
        <HoroscopePdfDocument
          chartResult={chartResult}
          lang={pdfLang}
          colorScheme={colorScheme}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildFilename(chartResult);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke after a delay to ensure download starts
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setError(isTa ? 'PDF உருவாக்கம் தோல்வி' : 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  }, [chartResult, pdfLang, colorScheme, generating, isTa]);

  if (!chartResult) return null;

  return (
    <PanelContainer>
      {/* Language selector */}
      <SelectorGroup>
        <SelectorLabel>{isTa ? 'PDF மொழி' : 'PDF Language'}</SelectorLabel>
        <OptionBtn active={pdfLang === 'en'} onClick={() => setPdfLang('en')}>English</OptionBtn>
        <OptionBtn active={pdfLang === 'ta'} onClick={() => setPdfLang('ta')}>தமிழ்</OptionBtn>
      </SelectorGroup>

      {/* Colour scheme selector */}
      <SelectorGroup>
        <SelectorLabel>{isTa ? 'வண்ண தீம்' : 'Theme'}</SelectorLabel>
        {schemeOptions.map(opt => (
          <OptionBtn
            key={opt.key}
            active={colorScheme === opt.key}
            onClick={() => setColorScheme(opt.key)}
          >
            {isTa ? opt.labelTa : opt.label}
          </OptionBtn>
        ))}
      </SelectorGroup>

      {/* Error */}
      {error && <ErrorText>{error}</ErrorText>}

      {/* Export button */}
      <ExportButton
        onClick={handleExport}
        disabled={generating}
        aria-label={isTa ? 'PDF ஏற்றுமதி' : 'Export PDF'}
      >
        {generating
          ? (isTa ? 'உருவாக்குகிறது...' : 'Generating...')
          : (isTa ? 'PDF ஏற்றுமதி' : 'Export PDF')
        }
      </ExportButton>
    </PanelContainer>
  );
};

export default PdfExportPanel;
